import axios from 'axios';
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { filterUrls } from 'config/urls';
import type { SimulatorFilterCategory, SimulatorFilterOption } from './SimulatorFilterPanel';
import {
    buildSimulatorFilterAttributeNamesInclusion,
    buildSimulatorFilterShowOnlyPayload,
    SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
    SIMULATOR_FILTER_FEATURE_NAME,
    type SimulatorFilterScopeItem,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';

export type { SimulatorFilterScopeItem };

type AttributeNamesApiRow = {
    attribute_name?: string;
    is_active?: boolean;
};

type AttributeNamesPage = {
    options: SimulatorFilterOption[];
    totalCount: number;
};

type UseSimulatorFilterAttributeNamesArgs = {
    category: SimulatorFilterCategory | null;
    appliedScope?: SimulatorFilterScopeItem[];
    showOnly?: SimulatorShowOnlyState;
    searchKeyword?: string;
    enabled?: boolean;
};

function parseGlobalFilterAttributeNamesPage(data: unknown): AttributeNamesPage {
    const payload = data as {
        data?: AttributeNamesApiRow[];
        total_count?: number;
    };
    if (!Array.isArray(payload?.data)) {
        return { options: [], totalCount: payload?.total_count ?? 0 };
    }

    const options = payload.data
        .map((item) => {
            const name = item.attribute_name?.trim();
            if (!name) return null;
            return { id: name, label: name };
        })
        .filter((option): option is SimulatorFilterOption => option !== null);

    return {
        options,
        totalCount: payload.total_count ?? options.length,
    };
}

function dedupeOptions(pages: AttributeNamesPage[]): SimulatorFilterOption[] {
    const byId = new Map<string, SimulatorFilterOption>();
    pages.forEach((page) => {
        page.options.forEach((option) => {
            if (!byId.has(option.id)) {
                byId.set(option.id, option);
            }
        });
    });
    return [...byId.values()];
}

type FetchAttributeNamesPageArgs = {
    attributeType: string;
    searchDimension: string;
    inclusionFilters: ReturnType<typeof buildSimulatorFilterAttributeNamesInclusion>;
    showOnlyPayload: ReturnType<typeof buildSimulatorFilterShowOnlyPayload>;
    searchKeyword: string;
    offset: number;
};

async function fetchAttributeNamesPage({
    attributeType,
    searchDimension,
    inclusionFilters,
    showOnlyPayload,
    searchKeyword,
    offset,
}: FetchAttributeNamesPageArgs): Promise<AttributeNamesPage> {
    const { data } = await axios.post(filterUrls.getAttributeNamesUrl, {
        attributeType,
        email: localStorage.getItem('email'),
        featureName: SIMULATOR_FILTER_FEATURE_NAME,
        filters: {
            inclusion: inclusionFilters,
            exclusion: [],
        },
        ...(showOnlyPayload ? { show_only: showOnlyPayload } : {}),
        limit: SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
        offset,
        searchDimension,
        searchKeyword,
    });

    return parseGlobalFilterAttributeNamesPage(data);
}

export async function fetchAllSimulatorFilterAttributeNames({
    category,
    appliedScope,
    showOnly,
}: {
    category: SimulatorFilterCategory;
    appliedScope?: SimulatorFilterScopeItem[];
    showOnly?: SimulatorShowOnlyState;
}): Promise<SimulatorFilterOption[]> {
    const attributeType = category.attributeType ?? category.key ?? '';
    const searchDimension = category.dimension ?? 'dim1';
    const inclusionFilters = buildSimulatorFilterAttributeNamesInclusion(appliedScope, attributeType);
    const showOnlyPayload = showOnly ? buildSimulatorFilterShowOnlyPayload(showOnly) : undefined;

    const pages: AttributeNamesPage[] = [];
    let offset = 0;
    let total = 0;

    do {
        const page = await fetchAttributeNamesPage({
            attributeType,
            searchDimension,
            inclusionFilters,
            showOnlyPayload,
            searchKeyword: '',
            offset,
        });
        pages.push(page);
        total = page.totalCount;
        offset += page.options.length;
        if (page.options.length === 0) break;
    } while (offset < total);

    return dedupeOptions(pages);
}

/** First page of attribute names for applied-filters select-all preview (max 50). */
export async function fetchSimulatorFilterAttributeNamesPreview({
    category,
    appliedScope,
    showOnly,
}: {
    category: SimulatorFilterCategory;
    appliedScope?: SimulatorFilterScopeItem[];
    showOnly?: SimulatorShowOnlyState;
}): Promise<SimulatorFilterOption[]> {
    const attributeType = category.attributeType ?? category.key ?? '';
    const searchDimension = category.dimension ?? 'dim1';
    const page = await fetchAttributeNamesPage({
        attributeType,
        searchDimension,
        inclusionFilters: buildSimulatorFilterAttributeNamesInclusion(appliedScope, attributeType),
        showOnlyPayload: showOnly ? buildSimulatorFilterShowOnlyPayload(showOnly) : undefined,
        searchKeyword: '',
        offset: 0,
    });

    return page.options.slice(0, SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT);
}

export function useSimulatorFilterAttributeNames({
    category,
    appliedScope,
    showOnly,
    searchKeyword = '',
    enabled = true,
}: UseSimulatorFilterAttributeNamesArgs) {
    const attributeType = category?.attributeType ?? category?.key ?? '';
    const searchDimension = category?.dimension ?? 'dim1';
    const inclusionFilters = buildSimulatorFilterAttributeNamesInclusion(appliedScope, attributeType);
    const showOnlyPayload = showOnly ? buildSimulatorFilterShowOnlyPayload(showOnly) : undefined;
    const inclusionQueryKey = JSON.stringify(inclusionFilters);
    const showOnlyQueryKey = JSON.stringify(showOnlyPayload ?? []);
    const normalizedSearchKeyword = searchKeyword.trim();

    const query = useInfiniteQuery<AttributeNamesPage>({
        queryKey: [
            'simulator-filter-attribute-names',
            searchDimension,
            attributeType,
            inclusionQueryKey,
            showOnlyQueryKey,
            normalizedSearchKeyword,
            SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
        ],
        enabled: enabled && Boolean(category) && Boolean(attributeType),
        initialPageParam: 0,
        queryFn: async ({ pageParam }) =>
            fetchAttributeNamesPage({
                attributeType,
                searchDimension,
                inclusionFilters,
                showOnlyPayload,
                searchKeyword: normalizedSearchKeyword,
                offset: pageParam as number,
            }),
        getNextPageParam: (lastPage, allPages) => {
            const loadedCount = allPages.reduce((sum, page) => sum + page.options.length, 0);
            if (loadedCount >= lastPage.totalCount) return undefined;
            if (lastPage.options.length < SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT) return undefined;
            return loadedCount;
        },
    });

    const options = useMemo(
        () => dedupeOptions(query.data?.pages ?? []),
        [query.data?.pages],
    );

    const totalCount = query.data?.pages[0]?.totalCount ?? 0;

    return {
        options,
        totalCount,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        isError: query.isError,
    };
}
